import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  INTERVAL_OPTIONS_SECONDS,
  WINDOW_OPTIONS_MINUTES,
  loadAutoDeactivateSettings,
  saveAutoDeactivateSettings
} from '../utils/autoDeactivateSettings';
import {
  Actions,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Header,
  HeaderIconButton,
  HeaderText,
  HomeIcon,
  Input,
  Page,
  Select,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import {
  DISTANCE_UNIT_OPTIONS,
  DISTANCE_UNITS,
  getDistanceUnit
} from '../utils/distanceUnits';
import {
  loadDefaultLocationRadius,
  saveDefaultLocationRadius
} from '../utils/defaultLocationRadius';
import { isPersonalApp } from '../utils/appMode';

const SettingsPanel = styled(Card)`
  display: grid;
  gap: 0.85rem;
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const SettingRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.045);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const SettingGroup = styled.div`
  display: grid;
  gap: 0;
  padding: 0.9rem;
  border: 1px solid rgba(251, 191, 36, 0.22);
  border-radius: 26px;
  background:
    linear-gradient(135deg, rgba(251, 191, 36, 0.08), rgba(96, 165, 250, 0.05)),
    rgba(255, 255, 255, 0.035);
`;

const GroupedSettingRow = styled(SettingRow)`
  border-color: transparent;
  border-radius: 18px;
  background: transparent;

  & + & {
    border-top-color: rgba(226, 232, 240, 0.11);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &:not(:last-child) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const ToggleSettingRow = styled(GroupedSettingRow)`
  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
  }
`;

const SettingText = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const SettingTitle = styled.span`
  color: #f8fafc;
  font-size: 1.1rem;
  font-weight: 950;
`;

const SettingDescription = styled.span`
  max-width: 34rem;
  color: rgba(203, 213, 225, 0.68);
  font-weight: 700;
  line-height: 1.35;
`;

const CompactSelect = styled(Select)`
  width: auto;
  min-width: 140px;
`;

const RadiusControls = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 130px) minmax(120px, 160px);
  gap: 0.65rem;

  @media (max-width: 640px) {
    width: 100%;
    grid-template-columns: 1fr;
  }
`;

const CompactInput = styled(Input)`
  min-width: 0;
`;

const Checkbox = styled.input`
  width: 26px;
  height: 26px;
  accent-color: #fbbf24;
`;

const DangerRow = styled(SettingRow)`
  border-color: rgba(248, 113, 113, 0.32);
  background: rgba(127, 29, 29, 0.18);
`;

const DangerButton = styled(Button)`
  background: linear-gradient(135deg, #ef4444, #991b1b);
  color: #fff7ed;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid rgba(248, 113, 113, 0.38);
  border-radius: 18px;
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
  font-weight: 850;
`;

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const canUsePersonalFeatures = isPersonalApp();
  const [settings, setSettings] = useState(loadAutoDeactivateSettings);
  const [defaultRadius, setDefaultRadius] = useState(loadDefaultLocationRadius);
  const [deleteError, setDeleteError] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const updateSettings = (updates) => {
    setSettings(saveAutoDeactivateSettings({
      ...settings,
      ...updates
    }));
  };

  const updateDefaultRadius = (updates) => {
    setDefaultRadius(saveDefaultLocationRadius({
      ...defaultRadius,
      ...updates
    }));
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm('Delete your PlacePledge account and all saved data? This cannot be undone.');

    if (!confirmed) {
      return;
    }

    setIsDeletingAccount(true);
    setDeleteError('');

    try {
      const idToken = await user.getIdToken();
      await axios.delete(`${window.RUNTIME_CONFIG.apiUrl}/user`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      try {
        await logout();
      } catch (error) {
        console.warn('Sign out after account deletion failed:', error);
      }

      navigate('/login', { replace: true });
    } catch (error) {
      setDeleteError(error.response?.data?.message || error.message || 'Failed to delete account');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Home" onClick={() => navigate('/dashboard')}>
                  <HomeIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Settings.</Title>
            <Subtitle>{user?.email || 'Guest account'}</Subtitle>
          </HeaderText>
        </Header>

        <SettingsPanel>
          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Manage pledges</SettingTitle>
              <SettingDescription>Edit or delete pledges during the 3-minute window after creation.</SettingDescription>
            </SettingText>
            <Button type="button" onClick={() => navigate('/manage-pledges')}>
              Manage
            </Button>
          </SettingRow>

          {canUsePersonalFeatures && (
            <SettingRow as="div">
              <SettingText>
                <SettingTitle>Locations</SettingTitle>
                <SettingDescription>Add, edit, or delete the places you can pledge to reach.</SettingDescription>
              </SettingText>
              <Button
                type="button"
                onClick={() => navigate('/manage-locations', { state: { backTo: '/settings' } })}
              >
                Manage
              </Button>
            </SettingRow>
          )}

          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Default radius</SettingTitle>
              <SettingDescription>Used for locations created from the pledge map.</SettingDescription>
            </SettingText>
            <RadiusControls>
              <CompactInput
                type="number"
                aria-label="Default radius value"
                min={getDistanceUnit(defaultRadius.unit).min}
                step={getDistanceUnit(defaultRadius.unit).step}
                value={defaultRadius.value}
                onChange={(event) => updateDefaultRadius({ value: event.target.value })}
              />
              <CompactSelect
                aria-label="Default radius unit"
                value={defaultRadius.unit}
                onChange={(event) => updateDefaultRadius({
                  unit: event.target.value,
                  value: DISTANCE_UNITS[event.target.value].defaultValue
                })}
              >
                {DISTANCE_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CompactSelect>
            </RadiusControls>
          </SettingRow>

          {canUsePersonalFeatures && (
            <SettingGroup>
              <ToggleSettingRow>
                <SettingText>
                  <SettingTitle>Auto fulfillment</SettingTitle>
                  <SettingDescription>Try to fulfill active pledges while the dashboard tab is visible.</SettingDescription>
                </SettingText>
                <Checkbox
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(event) => updateSettings({ enabled: event.target.checked })}
                />
              </ToggleSettingRow>

              {settings.enabled && (
                <>
                  <GroupedSettingRow>
                    <SettingText>
                      <SettingTitle>Try every</SettingTitle>
                      <SettingDescription>How often the dashboard checks whether a pledge can be fulfilled.</SettingDescription>
                    </SettingText>
                    <CompactSelect
                      value={settings.intervalSeconds}
                      onChange={(event) => updateSettings({ intervalSeconds: Number(event.target.value) })}
                    >
                      {INTERVAL_OPTIONS_SECONDS.map((seconds) => (
                        <option key={seconds} value={seconds}>
                          {seconds} sec
                        </option>
                      ))}
                    </CompactSelect>
                  </GroupedSettingRow>

                  <GroupedSettingRow>
                    <SettingText>
                      <SettingTitle>Start within</SettingTitle>
                      <SettingDescription>Only try fulfillment near the pledge time.</SettingDescription>
                    </SettingText>
                    <CompactSelect
                      value={settings.windowMinutes}
                      onChange={(event) => updateSettings({ windowMinutes: Number(event.target.value) })}
                    >
                      {WINDOW_OPTIONS_MINUTES.map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {minutes} min
                        </option>
                      ))}
                    </CompactSelect>
                  </GroupedSettingRow>
                </>
              )}
            </SettingGroup>
          )}

          <DangerRow as="div">
            <SettingText>
              <SettingTitle>Delete account and data</SettingTitle>
              <SettingDescription>Remove your account, pledges, contacts, locations, and saved settings.</SettingDescription>
            </SettingText>
            <DangerButton type="button" onClick={handleDeleteAccount} disabled={isDeletingAccount}>
              {isDeletingAccount ? 'Deleting...' : 'Delete my account & data'}
            </DangerButton>
          </DangerRow>

          {deleteError && <ErrorMessage>{deleteError}</ErrorMessage>}
        </SettingsPanel>
      </Shell>
    </Page>
  );
};

export default Settings;
